import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import SEO from '../../components/SEO';
import { 
  FileText, 
  Building2,
  ArrowLeft,
  Clock,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Download,
  Sparkles
} from 'lucide-react';

export default function GuideDCEArticle() {
  return (
    <>
      <SEO 
        title="Guide DCE : Comprendre le Dossier de Consultation"
        description="Qu'est-ce qu'un DCE ? Guide complet pour comprendre et analyser un Dossier de Consultation des Entreprises pour les marchés publics."
        path="/blog/guide-dce"
      />
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="container mx-auto px-4 py-3">
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700">
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>
        </div>
      </div>

      {/* Article Header */}
      <article>
        <header className="bg-gradient-to-b from-violet-50 to-white py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
                Guide
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-sm">
                <Calendar className="w-4 h-4" />
                1 février 2026
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-sm">
                <Clock className="w-4 h-4" />
                12 min de lecture
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Guide Complet du DCE : Dossier de Consultation des Entreprises
            </h1>
            
            <p className="text-xl text-slate-600">
              Le DCE est la clé pour répondre efficacement aux appels d'offres. 
              Découvrez sa composition, comment l'analyser et les pièges à éviter.
            </p>
          </div>
        </header>

        {/* Article Content */}
        <div className="container mx-auto px-4 max-w-4xl py-12">
          <div className="prose prose-lg prose-slate max-w-none">
            
            {/* Introduction */}
            <section className="mb-12">
              <p className="lead text-xl text-slate-700">
                Le <strong>DCE (Dossier de Consultation des Entreprises)</strong> est l'ensemble des documents 
                mis à disposition par l'acheteur public pour permettre aux entreprises de répondre à un 
                <strong> appel d'offres</strong>. Bien comprendre et analyser le DCE est crucial pour 
                maximiser vos chances de remporter un marché public.
              </p>
            </section>

            {/* Table of contents */}
            <Card className="bg-slate-50 mb-12">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-4">Dans cet article</h2>
                <ul className="space-y-2">
                  <li><a href="#composition" className="text-violet-600 hover:underline">1. Composition du DCE</a></li>
                  <li><a href="#pieces-principales" className="text-violet-600 hover:underline">2. Les pièces principales expliquées</a></li>
                  <li><a href="#analyser-dce" className="text-violet-600 hover:underline">3. Comment analyser un DCE</a></li>
                  <li><a href="#pieges" className="text-violet-600 hover:underline">4. Les pièges à éviter</a></li>
                  <li><a href="#obtenir-dce" className="text-violet-600 hover:underline">5. Comment obtenir un DCE rapidement</a></li>
                </ul>
              </CardContent>
            </Card>

            {/* Section 1 */}
            <section id="composition" className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-lg font-bold">1</span>
                Composition du DCE
              </h2>
              
              <p className="text-slate-600 mb-6">
                Un DCE complet contient généralement les documents suivants, chacun ayant un rôle spécifique 
                dans la procédure de passation du marché public :
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  { abbr: 'RC', name: 'Règlement de Consultation', desc: 'Les règles du jeu de la consultation' },
                  { abbr: 'AAPC', name: 'Avis d\'Appel Public à la Concurrence', desc: 'L\'annonce officielle du marché' },
                  { abbr: 'CCAP', name: 'Cahier des Clauses Administratives Particulières', desc: 'Les conditions contractuelles' },
                  { abbr: 'CCTP', name: 'Cahier des Clauses Techniques Particulières', desc: 'Le cahier des charges technique' },
                  { abbr: 'BPU', name: 'Bordereau des Prix Unitaires', desc: 'La grille tarifaire à compléter' },
                  { abbr: 'DPGF', name: 'Décomposition du Prix Global et Forfaitaire', desc: 'Le détail des coûts' },
                  { abbr: 'AE', name: 'Acte d\'Engagement', desc: 'Le document contractuel principal' },
                  { abbr: 'DC1/DC2', name: 'Formulaires de Candidature', desc: 'Documents administratifs obligatoires' },
                ].map((doc) => (
                  <Card key={doc.abbr} className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded text-sm font-bold">
                          {doc.abbr}
                        </span>
                        <div>
                          <p className="font-semibold text-slate-900">{doc.name}</p>
                          <p className="text-sm text-slate-500">{doc.desc}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Section 2 */}
            <section id="pieces-principales" className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-lg font-bold">2</span>
                Les pièces principales expliquées
              </h2>

              <h3 className="text-xl font-bold text-slate-900 mt-8 mb-4">Le Règlement de Consultation (RC)</h3>
              <p className="text-slate-600 mb-4">
                Le RC est votre <strong>feuille de route</strong>. Il définit :
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Les critères de sélection et leur pondération</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>La date et l'heure limite de remise des offres</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Les documents à fournir obligatoirement</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Les modalités de dépôt (plateforme, format)</span>
                </li>
              </ul>

              <h3 className="text-xl font-bold text-slate-900 mt-8 mb-4">Le CCTP (Cahier des Charges)</h3>
              <p className="text-slate-600 mb-4">
                Le CCTP décrit précisément <strong>ce que l'acheteur attend</strong>. Il contient :
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Les spécifications techniques détaillées</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Les performances attendues</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Les normes à respecter</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Le contexte et les contraintes du projet</span>
                </li>
              </ul>

              <Card className="bg-amber-50 border-amber-200 mb-8">
                <CardContent className="p-6">
                  <h4 className="font-bold text-amber-900 flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5" />
                    Conseil d'expert
                  </h4>
                  <p className="text-amber-800">
                    Lisez le CCTP <strong>plusieurs fois</strong>. La première lecture donne une vue d'ensemble, 
                    les suivantes permettent d'identifier les points critiques et les exigences cachées.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Section 3 */}
            <section id="analyser-dce" className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-lg font-bold">3</span>
                Comment analyser un DCE efficacement
              </h2>

              <p className="text-slate-600 mb-6">
                Une analyse méthodique du DCE est essentielle pour préparer une offre gagnante :
              </p>

              <ol className="space-y-6 mb-8">
                <li className="flex items-start gap-4">
                  <span className="w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
                  <div>
                    <h4 className="font-bold text-slate-900">Vérifiez votre éligibilité</h4>
                    <p className="text-slate-600">Assurez-vous de remplir toutes les conditions : certifications requises, 
                    références demandées, capacité financière minimale.</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
                  <div>
                    <h4 className="font-bold text-slate-900">Identifiez les critères de notation</h4>
                    <p className="text-slate-600">Notez la pondération de chaque critère (prix, valeur technique, délais) 
                    et structurez votre réponse en conséquence.</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
                  <div>
                    <h4 className="font-bold text-slate-900">Listez les documents à fournir</h4>
                    <p className="text-slate-600">Créez une checklist de tous les documents demandés pour ne rien oublier 
                    (cause fréquente de rejet).</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</span>
                  <div>
                    <h4 className="font-bold text-slate-900">Évaluez la rentabilité</h4>
                    <p className="text-slate-600">Estimez le coût de préparation de l'offre vs. les chances de succès 
                    et la marge potentielle.</p>
                  </div>
                </li>
              </ol>
            </section>

            {/* Section 4 */}
            <section id="pieges" className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-lg font-bold">4</span>
                Les pièges à éviter
              </h2>

              <div className="space-y-4 mb-8">
                {[
                  { title: 'Offre incomplète', desc: 'Un document manquant = offre rejetée. Vérifiez 3 fois votre dossier.' },
                  { title: 'Délai dépassé', desc: 'Même d\'une minute ! Soumettez 24h avant la date limite.' },
                  { title: 'Prix trop bas', desc: 'Une offre anormalement basse sera écartée ou vous perdrez de l\'argent.' },
                  { title: 'Hors sujet', desc: 'Répondez précisément à ce qui est demandé, pas à côté.' },
                  { title: 'Copier-coller', desc: 'Personnalisez chaque réponse, les acheteurs le voient immédiatement.' },
                ].map((trap, i) => (
                  <Card key={`item-${i}`} className="border-red-200 bg-red-50">
                    <CardContent className="p-4 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-red-900">{trap.title}</p>
                        <p className="text-sm text-red-700">{trap.desc}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Section 5 */}
            <section id="obtenir-dce" className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <span className="w-10 h-10 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-lg font-bold">5</span>
                Comment obtenir un DCE rapidement
              </h2>

              <p className="text-slate-600 mb-6">
                Les DCE sont disponibles sur les plateformes de dématérialisation des acheteurs publics. 
                Cependant, naviguer entre plusieurs plateformes peut être chronophage.
              </p>

              <Card className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white mb-8">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <Download className="w-10 h-10 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-bold mb-2">Service DCE sous 24h</h3>
                      <p className="text-white/90 mb-4">
                        Avec Mon DCE Express, commandez n'importe quel DCE et recevez-le directement 
                        par email sous 24 heures. Plus besoin de créer un compte sur chaque plateforme !
                      </p>
                      <div className="flex items-center gap-4">
                        <span className="text-2xl font-bold">À partir de 2€</span>
                        <Link to="/">
                          <Button className="bg-white text-violet-700 hover:bg-slate-100">
                            Découvrir le service
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Conclusion */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Conclusion</h2>
              <p className="text-slate-600 mb-4">
                Le DCE est le document fondamental de tout appel d'offres public. Une bonne maîtrise de son 
                contenu et une analyse méthodique sont les clés pour préparer des offres compétitives et 
                maximiser vos chances de succès.
              </p>
              <p className="text-slate-600">
                N'hésitez pas à utiliser des outils comme <strong>Mon DCE Express</strong> pour automatiser 
                votre veille et recevoir les DCE rapidement, vous permettant de vous concentrer sur 
                l'essentiel : la rédaction d'une offre gagnante.
              </p>
            </section>

          </div>
        </div>

        {/* CTA */}
        <section className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Trouvez votre prochain marché public
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Notre IA analyse 6+ sources officielles et vous recommande les appels d'offres 
              les plus pertinents pour votre entreprise.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100">
                <Sparkles className="w-5 h-5 mr-2" />
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </section>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="container mx-auto px-4 text-center text-sm">
          <p>© 2026 Mon DCE Express - Tous droits réservés</p>
        </div>
      </footer>
    </div>
    </>
  );
}
